import React from 'react';
import { companyStats } from '../mock';
import { Calendar, Users, Globe, Package, Building2, Factory, Award, TrendingUp, Target, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const getIcon = (iconName) => {
    const icons = {
      calendar: Calendar,
      users: Users,
      globe: Globe,
      package: Package,
      building: Building2,
      factory: Factory
    };
    const Icon = icons[iconName] || Package;
    return <Icon className="w-12 h-12" />;
  };

  const values = [
    {
      icon: Award,
      title: 'Kalite',
      description: 'Yüksek kalite standartlarında üretim ve müşteri memnuniyeti bizim önceliğimizdir.'
    },
    {
      icon: TrendingUp,
      title: 'Sürekli Gelişim',
      description: 'Teknoloji ve inovasyona yatırım yaparak sürekli gelişmeyi hedefliyoruz.'
    },
    {
      icon: Target,
      title: 'Müşteri Odaklılık',
      description: 'Müşterilerimizin ihtiyaçlarını anlamak ve en iyi çözümleri sunmak için çalışıyoruz.'
    },
    {
      icon: Heart,
      title: 'Sürdürülebilirlik',
      description: 'Çevreye saygılı üretim süreçleri ile gelecek nesillere daha iyi bir dünya bırakmayı amaçlıyoruz.'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden mb-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?w=1920&q=80)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-4">Hakkımızda</h1>
            <p className="text-xl max-w-2xl">
              15 yılı aşkın tecrübemizle plastik ambalaj sektöründe öncü firmalardan biriyiz.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Company Story */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Hikayemiz
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  KargoPack, 2010 yılında İstanbul'da kuruldu ve kısa sürede Türkiye'nin önde gelen plastik ambalaj üreticilerinden biri haline geldi. E-ticaretin hızla büyüdüğü bu dönemde, kaliteli ve uygun fiyatlı kargo poşeti ihtiyacını karşılamak için yola çıktık.
                </p>
                <p>
                  Bugün ülkemizde ki birçok e-ticaret firmaları ve dağıtım yapan kargo firmalarının çözüm ortağı olmakla beraber, 10+ ülkeye yaptığımız ihracat ile de ülkemize katma değer sağlıyoruz.
                </p>
                <p>
                  Kalite, hız ve müşteri memnuniyeti odaklı çalışma anlayışımızla, sektörde fark yaratmaya devam ediyoruz. Sürdürülebilir üretim süreçlerimiz ve çevre dostu yaklaşımımızla gelecek nesillere daha yaşanabilir bir dünya bırakmayı amaçlıyoruz.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#E31E24]/20 to-blue-400/20 rounded-3xl blur-3xl" />
              <img
                src="https://images.unsplash.com/photo-1685119166946-d4050647b0e3?w=800&q=80"
                alt="Fabrika"
                className="relative rounded-3xl shadow-2xl w-full"
              />
            </div>
          </div>
        </section>

        {/* Company Stats */}
        <section className="py-16 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl mb-20">
          <div className="max-w-6xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Rakamlarla KargoPack</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {companyStats.map((stat) => (
                <div key={stat.id} className="text-center">
                  <div className="flex justify-center mb-4 text-[#E31E24]">
                    {getIcon(stat.icon)}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Değerlerimiz</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Şirketimizi ayakta tutan ve ileriye taşıyan temel değerlerimiz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 border-0">
                <CardContent className="p-8">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                    <value.icon className="w-10 h-10 text-[#E31E24]" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-gray-700">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="grid md:grid-cols-2 gap-8">
          <Card className="bg-gradient-to-br from-[#E31E24] to-[#C01A1F] text-white border-0 hover:shadow-2xl transition-all duration-300">
            <CardContent className="p-8">
              <h3 className="text-3xl font-bold mb-6">Misyonumuz</h3>
              <p className="text-lg leading-relaxed">
                Yüksek kaliteli, çevre dostu ve ekonomik kargo poşeti çözümleri sunarak müşterilerimizin iş süreçlerini kolaylaştırmak ve değer katmak. Sektörde kalite ve güvenilirlik standartlarını yükseltmek.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-gray-800 to-gray-900 text-white border-0 hover:shadow-2xl transition-all duration-300">
            <CardContent className="p-8">
              <h3 className="text-3xl font-bold mb-6">Vizyonumuz</h3>
              <p className="text-lg leading-relaxed">
                Küresel ölçekte tanınan, sürdürülebilir üretim anlayışıyla öncü bir plastik ambalaj firması olmak. İnovasyon ve teknoloji odaklı yaklaşımımızla sektörün geleceğini şekillendirmek.
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default About;
